export const DEFAULT_TASK_SCHEDULE = Object.freeze({
  has_time: false,
  reminder_day_before: true,
  reminder_morning: true,
  reminder_two_hours: true,
});

const WALL_DATE_TIME_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,6})?)?)?(?:Z|[+-]\d{2}:?\d{2})?$/;

function pad2(value) {
  return String(value).padStart(2, '0');
}

export function taskScheduleParts(value) {
  if (value == null || String(value).trim() === '') return null;
  const raw = String(value).trim();
  const match = raw.match(WALL_DATE_TIME_RE);
  if (match) {
    return {
      date: `${match[1]}-${match[2]}-${match[3]}`,
      time: `${match[4] || '00'}:${match[5] || '00'}`,
    };
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  return {
    date: `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`,
    time: `${pad2(date.getHours())}:${pad2(date.getMinutes())}`,
  };
}

export function taskDateKey(value) {
  return taskScheduleParts(value)?.date || '';
}

export function taskTimeValue(value) {
  return taskScheduleParts(value)?.time || '09:00';
}

export function taskHasTime(task) {
  if (typeof task?.has_time === 'boolean') return task.has_time;
  const parts = taskScheduleParts(task?.due_date);
  return Boolean(parts && parts.time !== '00:00');
}

export function taskDueDateForForm(task) {
  const parts = taskScheduleParts(task?.due_date);
  if (!parts) return '';
  return `${parts.date}T${taskHasTime(task) ? parts.time : '00:00'}`;
}

export function taskScheduleFormFields(task) {
  return {
    has_time: taskHasTime(task),
    reminder_day_before: task?.reminder_day_before !== false,
    reminder_morning: task?.reminder_morning !== false,
    reminder_two_hours: task?.reminder_two_hours !== false,
  };
}

export function buildTaskDueDate(dateKey, hasTime, timeValue = '09:00') {
  if (!dateKey) return '';
  return `${dateKey}T${hasTime ? (timeValue || '09:00') : '00:00'}`;
}

/**
 * CRM работает по Москве. Россия не использует переход на летнее время,
 * поэтому московское календарное время стабильно равно UTC+3.
 */
export function moscowWallDate(dateKey, timeValue = '00:00') {
  const dateMatch = String(dateKey || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const timeMatch = String(timeValue || '').match(/^(\d{2}):(\d{2})$/);
  if (!dateMatch || !timeMatch) return null;
  const [, year, month, day] = dateMatch;
  const [, hour, minute] = timeMatch;
  const timestamp = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour) - 3,
    Number(minute),
    0,
    0
  );
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function buildTaskReminderPlans(task) {
  const parts = taskScheduleParts(task?.due_date);
  if (!parts || task?.status === 'completed') return [];

  const hasTime = taskHasTime(task);
  const dueAt = hasTime
    ? moscowWallDate(parts.date, parts.time)
    : moscowWallDate(parts.date, '10:00');
  const morningAt = moscowWallDate(parts.date, '10:00');
  if (!dueAt || !morningAt) return [];

  const plans = [];
  if (task?.reminder_day_before !== false) {
    plans.push({
      slot: 'day-before',
      at: new Date(dueAt.getTime() - 24 * 60 * 60 * 1000),
      title: 'Задача завтра',
    });
  }
  if (task?.reminder_morning !== false && (!hasTime || dueAt.getTime() > morningAt.getTime())) {
    plans.push({
      slot: 'morning',
      at: morningAt,
      title: 'Задача сегодня',
    });
  }
  if (hasTime && task?.reminder_two_hours !== false) {
    plans.push({
      slot: 'two-hours',
      at: new Date(dueAt.getTime() - 2 * 60 * 60 * 1000),
      title: 'Задача через 2 часа',
    });
  }
  return plans;
}
